import { useCallback, useState, useEffect } from "react";
import SelectBox from "../../SelectBox";
import styles from "./styles.module.scss";

const today = new Date();
const endYear = today.getFullYear() - 18;
const endMonthIndex = today.getMonth();
const endDay = today.getDate();

const usMonths = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

function getValidYears() {
  return Array.from({ length: endYear - 1920 + 1 }, (_, i) =>
    String(endYear - i)
  );
}

function getValidMonths(year) {
  if (year === endYear) {
    return usMonths.slice(0, endMonthIndex + 1);
  }
  return usMonths;
}

function getValidDays(year, month) {
  if (month === -1) {
    return Array.from({ length: 31 }, (_, i) => String(i + 1));
  }
  // use a fallback year to determine accurate daysInMonth when the year is not yet selected
  const defaultYear = year || 1981;

  if (defaultYear === endYear && month === endMonthIndex) {
    return Array.from({ length: endDay }, (_, i) => String(i + 1));
  }

  const daysInMonth = new Date(defaultYear, month + 1, 0).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => String(i + 1));
}

export default function BirthDate({ date, onDateComplete }) {
  const initialDate = date ? date.split("-").map(Number) : [];
  const [year, setYear] = useState(initialDate[0] || "");
  const [month, setMonth] = useState(initialDate[1] ? initialDate[1] - 1 : ""); // months are 0-indexed
  const [day, setDay] = useState(initialDate[2] || "");
  const memoizedOnDateComplete = useCallback(onDateComplete, []);

  useEffect(() => {
    if (date) {
      const [year, month, day] = date.split("-").map(Number);
      setYear(year);
      setDay(day);
      setMonth(month - 1);
    }
  }, [date]);

  useEffect(() => {
    if (year && month !== -1 && day) {
      const selectedDate = new Date(year, month, day);
      memoizedOnDateComplete(selectedDate);
    }
  }, [year, month, day, memoizedOnDateComplete]);

  useEffect(() => {
    const daysInMonth = getValidDays(year, month);
    if (day && !daysInMonth.includes(String(day))) {
      setDay(1);
    }
  }, [year, month]);

  const handleMonthChange = selectedMonth => {
    const newMonth = usMonths.indexOf(selectedMonth);
    const daysInMonth = getValidDays(year, newMonth);
    if (day && !daysInMonth.includes(String(day))) {
      setDay(1);
    }
    setMonth(newMonth);
  };

  const validYears = getValidYears();
  const validMonths = getValidMonths(year);
  const validDays = getValidDays(year, month);

  return (
    <section className={styles.birthdate}>
      <SelectBox
        options={validMonths}
        placeholder="Month"
        ariaLabel="Month"
        onChange={handleMonthChange}
        selection={usMonths[month]}
        wrapperClassName={styles.container}
        selectClassName={styles.selectTrigger}
        dataAttribute="month-select-trigger"
      />
      <SelectBox
        options={validDays}
        placeholder="Day"
        ariaLabel="Day"
        onChange={day => setDay(parseInt(day))}
        selection={String(day)}
        wrapperClassName={styles.container}
        selectClassName={styles.selectTrigger}
        dataAttribute="day-select-trigger"
      />
      <SelectBox
        options={validYears}
        placeholder="Year"
        ariaLabel="Year"
        onChange={year => setYear(parseInt(year))}
        selection={String(year)}
        wrapperClassName={styles.container}
        selectClassName={styles.selectTrigger}
        dataAttribute="year-select-trigger"
      />
    </section>
  );
}
